import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EmployeeOrderBy, Note, TeamMeetingState } from "@acto/gql/graphql";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useOpenTodosData, useTeamKpis, useTeamLineThresholds } from "@acto/taskpane/core/data-access/team";
import { ManagementDataTable } from "../ui/DataTable/ManagementDataTable";
import { ManagementDataTableSkeleton } from "../ui/DataTable/ManagementDataTableSkeleton";
import type { Column, Row, RowGrouping } from "../ui/DataTable/types";
import { type KPIHeaderColumn, KPIHeaders } from "../ui/KPIHeaders/KPIHeaders";
import { KPIHeadersSkeleton } from "../ui/KPIHeaders/KPIHeadersSkeleton";
import { PageHeader } from "../ui/PageHeader/PageHeader";
import { ManagementSearchAndFilterBar } from "../ui/SearchAndFilterBar/ManagementSearchAndFilterBar";
import { Flex } from "@acto/taskpane/core/components/flex/Flex";
import { useDebouncedSearchTerm } from "@acto/taskpane/core/utils/hooks/useDebouncedSearchTerm";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";
import { ManagementSidepanel } from "../ui/Sidepanel/ManagementSidepanel";
import {
  APPOINTMENT_STATE_BY_ACTIVITY,
  type ActivityType,
  EmployeeSidePanel,
} from "../ui/SidePanelContent/EmployeeSidePanel/EmployeeSidePanel";
import { EmployeeAppointmentStateSidePanel } from "../ui/SidePanelContent/AppointmentStateSidePanel";
import { DismissedCustomersSidePanel } from "../ui/SidePanelContent/DismissedCustomersSidePanel";
import { EmployeeMarkedDoneSidePanel } from "../ui/SidePanelContent/MarkedDoneSidePanel";
import { OpenTodosSidePanel, OpenTodosSidePanelSkeleton } from "../ui/SidePanelContent/OpenTodosSidePanel";
import { TodoSidePanel } from "../ui/SidePanelContent/TodoSidePanel";
import { CustomerDetailsSidePanel } from "../ui/SidePanelContent/CustomerDetailsSidePanel/CustomerDetailsSidePanel";
import { CustomerRecapsSidePanel } from "../ui/SidePanelContent/CustomerRecapsSidePanel";
import { useCustomerById } from "@acto/taskpane/core/data-access/customers/useCustomerById";
import { DateRange, PeriodOption } from "../ui/DateRangeSelector/types";
import { isComparableWindow, periodToDateRange } from "../utils/periodToDateRange";
import { DateRangeSelector } from "../ui/DateRangeSelector/DateRangeSelector";
import { PaddingWrapper, Divider } from "../ui/layout";
import { formatCurrency, formatChange, formatProfitWithMargin } from "../utils/formatters";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { KpiComparisonMode, type TeamLine, teamLineFor } from "@acto/taskpane/core/domain";
import { formatComparisonRange } from "../utils/dateFormatters";
import { useSortHandler } from "../hooks/useSortHandler";
import { useOrderBy } from "../hooks/useOrderBy";
import { MANAGEMENT_TODO_TYPES } from "../utils/managementTodoTypes";
import { useCascadingPeriod } from "../hooks/useCascadingPeriod";
import {
  type ProductDrillRoot,
  type ProductGroupRef,
  ProductDrilldownSidePanel,
} from "../ui/SidePanelContent/ProductDrilldownSidePanel";
import type { CustomerMetric } from "../ui/SidePanelContent/CustomerDetailsSidePanel/customerMetric";
import { AppointmentStatesCell } from "../ui/AppointmentStatesCell";
import { CellValue } from "../ui/DataTable/ui/CellValue";
import { EmployeeNameCell } from "../ui/EmployeeNameCell";

const NO_REGION = "\u0000";

const NAME_COLUMN_WIDTH = "26%";

const ACTIVITY_BY_LINE: Record<TeamLine["kind"], ActivityType | null> = {
  noActivity: null,
  noReport: "noReportAppointments",
  didNotTakePlace: "didNotTakePlaceAppointments",
};

const columnKeyToOrderBy: Record<string, { asc: EmployeeOrderBy; desc: EmployeeOrderBy }> = {
  name: { asc: EmployeeOrderBy.NameAsc, desc: EmployeeOrderBy.NameDesc },
  revenue: {
    asc: EmployeeOrderBy.RevenueAsc,
    desc: EmployeeOrderBy.RevenueDesc,
  },
  profit: { asc: EmployeeOrderBy.ProfitAsc, desc: EmployeeOrderBy.ProfitDesc },
  appointments: {
    asc: EmployeeOrderBy.AppointmentsAsc,
    desc: EmployeeOrderBy.AppointmentsDesc,
  },
  dismissals: {
    asc: EmployeeOrderBy.DismissalsAsc,
    desc: EmployeeOrderBy.DismissalsDesc,
  },
  markedDone: {
    asc: EmployeeOrderBy.MarkedDoneAsc,
    desc: EmployeeOrderBy.MarkedDoneDesc,
  },
};

export const TeamPage = () => {
  const t = useTranslations("team");
  const tKpi = useTranslations("kpi");
  const comparisonMode = useComparisonModeContext();
  const subtitle =
    comparisonMode === KpiComparisonMode.PERIOD_OVER_PERIOD ? t("subtitlePeriodOverPeriod") : t("subtitleYearOverYear");
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [activeActivity, setActiveActivity] = useState<ActivityType | null>(null);
  const [level3ClientId, setLevel3ClientId] = useState<string | null>(null);

  const [selectedCustomerIdFromEmployee, setSelectedCustomerIdFromEmployee] = useState<string | null>(null);
  type CustomerActivity = "productGroup" | "completedRecaps";
  const [customerActivity, setCustomerActivity] = useState<CustomerActivity | null>(null);
  const [customerActivityContext, setCustomerActivityContext] = useState<string | null>(null);
  const [customerLevel3ClientId, setCustomerLevel3ClientId] = useState<string | null>(null);
  const [customerProductDrillRoot, setCustomerProductDrillRoot] = useState<ProductDrillRoot | null>(null);

  const { sortColumn, sortDirection, handleSort } = useSortHandler("name", "asc");
  const {
    displayValue: searchValue,
    setDisplayValue: setSearchValue,
    debouncedValue: debouncedSearchTerm,
  } = useDebouncedSearchTerm();

  const track = useTrack();

  const [selectedPeriod, setSelectedPeriod] = useState(PeriodOption.THIS_MONTH);
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);

  const effectiveDateRange = useMemo(
    () => periodToDateRange(selectedPeriod, customDateRange),
    [selectedPeriod, customDateRange],
  );

  const showTrends = isComparableWindow(selectedPeriod, customDateRange);

  const employeePeriod = useCascadingPeriod(selectedPeriod, customDateRange, selectedEmployeeId);

  const appointmentsPeriod = useCascadingPeriod(employeePeriod.period, employeePeriod.customDateRange, activeActivity);

  const customerDetailPeriod = useCascadingPeriod(selectedPeriod, customDateRange, selectedCustomerIdFromEmployee);

  const customerLevel2Period = useCascadingPeriod(
    customerDetailPeriod.period,
    customerDetailPeriod.customDateRange,
    customerActivity,
  );

  const orderBy = useOrderBy(sortColumn, sortDirection, columnKeyToOrderBy, EmployeeOrderBy.NameAsc);

  const { teamKpis, employees, hasNextPage, fetchNextPage, isPending, isFetching, previousRange, regionGroups } =
    useTeamKpis({
      dateRange: effectiveDateRange,
      orderBy,
      searchTerm: debouncedSearchTerm || undefined,
      todoTypes: MANAGEMENT_TODO_TYPES,
      withAppointmentStates: true,
    });
  const lineThresholds = useTeamLineThresholds();

  const comparisonWindowsLabel = useMemo(() => {
    if (!showTrends) return null;
    const current = formatComparisonRange(effectiveDateRange, locale);
    if (!previousRange) return `(${current})`;
    return tKpi("comparisonWindows", {
      current,
      previous: formatComparisonRange(previousRange, locale),
    });
  }, [showTrends, effectiveDateRange, previousRange, locale, tKpi]);

  const employeesLengthRef = useRef(employees.length);
  employeesLengthRef.current = employees.length;

  useEffect(() => {
    if (debouncedSearchTerm) {
      track(AnalyticsEvents.MV_SEARCH_EMPLOYEE, {
        searchTerm: debouncedSearchTerm,
        resultCount: employeesLengthRef.current,
      });
    }
  }, [debouncedSearchTerm, track]);

  const appointmentStatesById = new Map(
    employees.flatMap((emp) => {
      const states = emp.activityMetrics?.appointmentStates;
      return states ? [[emp.id, states] as const] : [];
    }),
  );

  const appointmentsColumn: Column = {
    key: "appointments",
    label: t("columns.appointments"),
    type: "number",
    sortable: true,
    renderCell: (row) => {
      const states = appointmentStatesById.get(row.id);
      return states ? <AppointmentStatesCell states={states} /> : <CellValue value={row.appointments} type="number" />;
    },
  };

  const markedDoneColumns: Column[] = [
    { key: "markedDone", label: t("columns.markedDone"), type: "number", sortable: true },
  ];

  const employeesById = new Map(employees.map((emp) => [emp.id, emp]));

  const lineText = (line: TeamLine) => {
    switch (line.kind) {
      case "noActivity":
        return t("line.noActivity");
      case "noReport":
        return t("line.noReport", { count: line.count, due: line.due });
      case "didNotTakePlace":
        return t("line.didNotTakePlace", { count: line.count });
    }
  };

  const nameColumn: Column = lineThresholds
    ? {
        key: "name",
        label: t("columns.name"),
        type: "string",
        sortable: true,
        width: NAME_COLUMN_WIDTH,
        renderCell: (row) => {
          const employee = employeesById.get(row.id);
          const line = employee ? teamLineFor(employee.activityMetrics, lineThresholds) : null;
          return (
            <EmployeeNameCell
              name={String(row.name)}
              line={line && { text: lineText(line), onOpen: () => handleLineOpen(row.id, ACTIVITY_BY_LINE[line.kind]) }}
            />
          );
        },
      }
    : { key: "name", label: t("columns.name"), type: "string", sortable: true };

  const memberCountByRegion = new Map(regionGroups?.map((group) => [group.region ?? NO_REGION, group.memberCount]));

  const regionGrouping: RowGrouping | undefined = regionGroups
    ? {
        groupOf: (row) => employeesById.get(row.id)?.region ?? NO_REGION,
        labelOf: (region) => {
          const count = memberCountByRegion.get(region) ?? 0;
          return region === NO_REGION ? t("regionGroup.none", { count }) : t("regionGroup.named", { region, count });
        },
      }
    : undefined;

  const columns: Column[] = [
    nameColumn,
    {
      key: "revenue",
      label: t("columns.revenue"),
      type: "monetary",
      sortable: true,
    },
    {
      key: "profit",
      label: t("columns.margin"),
      type: "monetary",
      sortable: true,
    },
    appointmentsColumn,
    ...markedDoneColumns,
    {
      key: "dismissals",
      label: t("columns.notInterested"),
      type: "number",
      sortable: true,
    },
  ];

  const rows: Row[] = employees.map((emp) => ({
    id: emp.id,
    name: emp.name,
    revenue: {
      amount: formatCurrency(emp.revenue.totalValue, locale, currency),
      percentage: showTrends ? formatChange(emp.revenue.percentageChange) : "",
    },
    profit: {
      amount: formatProfitWithMargin(emp.profit.totalValue, emp.margin.totalValue, locale, currency),
      percentage: showTrends ? formatChange(emp.profit.percentageChange) : "",
    },
    appointments: emp.activityMetrics?.appointmentStates?.total ?? emp.activityMetrics?.meetingsCount ?? 0,
    dismissals: emp.activityMetrics?.dismissalsCount ?? 0,
    markedDone: emp.activityMetrics?.markedDoneCount ?? 0,
  }));

  const kpiHeaders: KPIHeaderColumn[] = useMemo(() => {
    if (!teamKpis) return [];
    return [
      {
        title: t("columns.revenue"),
        value: formatCurrency(teamKpis.revenue.totalValue, locale, currency),
        percentageChange: showTrends ? (teamKpis.revenue.percentageChange ?? undefined) : undefined,
      },
      {
        title: t("columns.margin"),
        value: formatProfitWithMargin(teamKpis.profit.totalValue, teamKpis.margin.totalValue, locale, currency),
        percentageChange: showTrends ? (teamKpis.profit.percentageChange ?? undefined) : undefined,
      },
      {
        title: t("columns.appointments"),
        value: String(
          teamKpis.activityMetrics?.appointmentStates?.total ?? teamKpis.activityMetrics?.meetingsCount ?? 0,
        ),
      },
      {
        title: t("kpiHeaders.notInterested"),
        value: String(teamKpis.activityMetrics?.dismissalsCount ?? 0),
      },
    ];
  }, [teamKpis, locale, currency, t, showTrends]);

  const selectedEmployee = employees.find((emp) => emp.id === selectedEmployeeId) ?? null;

  const selectedAppointmentStates = selectedEmployee?.activityMetrics?.appointmentStates;
  const activeAppointmentState =
    selectedAppointmentStates && activeActivity ? (APPOINTMENT_STATE_BY_ACTIVITY[activeActivity] ?? null) : null;

  const openTodosData = useOpenTodosData({
    employeeId: selectedEmployeeId,
    todoTypes: MANAGEMENT_TODO_TYPES,
    employeeName: selectedEmployee?.name ?? "",
  });

  const { data: selectedCustomerFromEmployee } = useCustomerById(selectedCustomerIdFromEmployee ?? "");

  const [selectedNote, setSelectedNote] = useState<Note | undefined>(undefined);

  const handleActivityClick = (activity: ActivityType) => {
    setActiveActivity(activity);
    setLevel3ClientId(null);
  };

  const handleLevel3Open = useCallback((clientId: string) => {
    setLevel3ClientId(clientId);
  }, []);

  const handleCloseLevel3 = () => {
    setLevel3ClientId(null);
  };

  const handleCloseLevel2 = () => {
    setActiveActivity(null);
    setLevel3ClientId(null);
  };

  const handleCloseLevel1 = () => {
    setSelectedEmployeeId(null);
    setActiveActivity(null);
    setLevel3ClientId(null);
  };

  const handleCompletedAppointmentClick = (noteId: string | null, clientId: string) => {
    handleLevel3Open(clientId);
  };

  const handleEmployeeRowClick = (employeeId: string) => {
    setSelectedCustomerIdFromEmployee(null);
    setCustomerActivity(null);
    setCustomerActivityContext(null);
    setCustomerLevel3ClientId(null);
    setSelectedNote(undefined);
    setSelectedEmployeeId(employeeId);
  };

  function handleLineOpen(employeeId: string, activity: ActivityType | null) {
    handleEmployeeRowClick(employeeId);
    setActiveActivity(activity);
    setLevel3ClientId(null);
  }

  const handleCustomerClickFromEmployee = (customerId: string, customerName: string) => {
    track(AnalyticsEvents.MV_OPEN_CUSTOMER, { customerId, customerName });
    setSelectedEmployeeId(null);
    setActiveActivity(null);
    setLevel3ClientId(null);
    setSelectedCustomerIdFromEmployee(customerId);
  };

  const handleEmployeeClickFromCustomer = (employeeId: string) => {
    if (!employees.find((emp) => emp.id === employeeId)) return;
    setSelectedCustomerIdFromEmployee(null);
    setCustomerActivity(null);
    setCustomerActivityContext(null);
    setCustomerLevel3ClientId(null);
    setSelectedNote(undefined);
    setSelectedEmployeeId(employeeId);
  };

  const handleCustomerProductGroupOpen = (group: ProductGroupRef, metric: CustomerMetric) => {
    setCustomerActivity("productGroup");
    setCustomerActivityContext(group.name);
    setCustomerLevel3ClientId(null);
    setCustomerProductDrillRoot({ group, initialMetric: metric });
  };

  const handleCustomerRecapsClick = () => {
    setCustomerActivity("completedRecaps");
    setCustomerActivityContext(null);
    setCustomerLevel3ClientId(null);
  };

  const handleCustomerRecapAppointmentClick = (note: Note | undefined, clientId: string) => {
    setSelectedNote(note);
    setCustomerLevel3ClientId(clientId);
  };

  const handleCloseCustomerLevel3 = () => {
    setCustomerLevel3ClientId(null);
    setSelectedNote(undefined);
  };

  const handleCloseCustomerLevel2 = () => {
    setCustomerActivity(null);
    setCustomerActivityContext(null);
    setCustomerLevel3ClientId(null);
  };

  const handleCloseCustomerLevel1 = () => {
    setSelectedCustomerIdFromEmployee(null);
    setCustomerActivity(null);
    setCustomerActivityContext(null);
    setCustomerLevel3ClientId(null);
    setSelectedNote(undefined);
  };

  return (
    <>
      <PaddingWrapper>
        <PageHeader
          title={t("title")}
          subtitle={showTrends ? subtitle : undefined}
          dateRangeLabel={comparisonWindowsLabel}
          showDateRangeSelector
          selectedPeriod={selectedPeriod}
          customDateRange={customDateRange}
          onPeriodChange={setSelectedPeriod}
          onCustomDateRangeChange={setCustomDateRange}
        />
      </PaddingWrapper>

      <Flex direction="column" gap={6}>
        <PaddingWrapper direction="column" gap={6}>
          {isFetching ? <KPIHeadersSkeleton /> : kpiHeaders.length > 0 && <KPIHeaders columns={kpiHeaders} />}
          {(isFetching || kpiHeaders.length > 0) && <Divider />}
          <ManagementSearchAndFilterBar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder={t("searchPlaceholder")}
            filterDropdowns={[]}
          />
        </PaddingWrapper>
        {isPending ? (
          <ManagementDataTableSkeleton
            columns={columns}
            rowCount={10}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
          />
        ) : (
          <ManagementDataTable
            columns={columns}
            rows={rows}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            hasMore={hasNextPage}
            onLoadMore={fetchNextPage}
            loadMoreLabel={t("loadMore")}
            isLoadingMore={isFetching}
            onRowClick={handleEmployeeRowClick}
            grouping={regionGrouping}
          />
        )}
      </Flex>
      <ManagementSidepanel
        isOpen={selectedEmployee !== null}
        onClose={handleCloseLevel1}
        level={1}
        isLevel1Shrunk={activeActivity !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={employeePeriod.period}
            customDateRange={employeePeriod.customDateRange}
            onPeriodChange={employeePeriod.setPeriod}
            onCustomDateRangeChange={employeePeriod.setCustomDateRange}
          />
        }
      >
        {selectedEmployee && (
          <EmployeeSidePanel
            employee={selectedEmployee}
            locale={locale}
            onActivityClick={handleActivityClick}
            onCustomerClick={handleCustomerClickFromEmployee}
            activeActivity={activeActivity}
            selectedPeriod={employeePeriod.period}
            selectedCustomDateRange={employeePeriod.customDateRange}
          />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel
        isOpen={selectedEmployee !== null && activeAppointmentState !== null}
        onClose={handleCloseLevel2}
        level={2}
        isLevel2Shrunk={level3ClientId !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={appointmentsPeriod.period}
            customDateRange={appointmentsPeriod.customDateRange}
            onPeriodChange={appointmentsPeriod.setPeriod}
            onCustomDateRangeChange={appointmentsPeriod.setCustomDateRange}
            excludedPeriods={[]}
          />
        }
      >
        {selectedEmployee && activeAppointmentState && (
          <EmployeeAppointmentStateSidePanel
            state={activeAppointmentState}
            employeeId={selectedEmployee.id}
            employeeName={selectedEmployee.name}
            selectedPeriod={appointmentsPeriod.period}
            selectedCustomDateRange={appointmentsPeriod.customDateRange}
            onAppointmentClick={
              activeAppointmentState === TeamMeetingState.Documented ? handleCompletedAppointmentClick : undefined
            }
            onCustomerClick={handleCustomerClickFromEmployee}
          />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel
        isOpen={selectedEmployee !== null && activeActivity === "ignoredSignals"}
        onClose={handleCloseLevel2}
        level={2}
        isLevel2Shrunk={level3ClientId !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={appointmentsPeriod.period}
            customDateRange={appointmentsPeriod.customDateRange}
            onPeriodChange={appointmentsPeriod.setPeriod}
            onCustomDateRangeChange={appointmentsPeriod.setCustomDateRange}
            excludedPeriods={[]}
          />
        }
      >
        {selectedEmployee && (
          <DismissedCustomersSidePanel
            employeeId={selectedEmployee.id}
            employeeName={selectedEmployee.name}
            selectedPeriod={appointmentsPeriod.period}
            selectedCustomDateRange={appointmentsPeriod.customDateRange}
            onCustomerClick={handleCustomerClickFromEmployee}
          />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel
        isOpen={selectedEmployee !== null && activeActivity === "markedDone"}
        onClose={handleCloseLevel2}
        level={2}
        isLevel2Shrunk={level3ClientId !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={appointmentsPeriod.period}
            customDateRange={appointmentsPeriod.customDateRange}
            onPeriodChange={appointmentsPeriod.setPeriod}
            onCustomDateRangeChange={appointmentsPeriod.setCustomDateRange}
            excludedPeriods={[]}
          />
        }
      >
        {selectedEmployee && (
          <EmployeeMarkedDoneSidePanel
            employeeId={selectedEmployee.id}
            employeeName={selectedEmployee.name}
            selectedPeriod={appointmentsPeriod.period}
            selectedCustomDateRange={appointmentsPeriod.customDateRange}
            onCustomerClick={handleCustomerClickFromEmployee}
          />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel
        isOpen={selectedEmployee !== null && activeActivity === "openTodos"}
        onClose={handleCloseLevel2}
        level={2}
        isLevel2Shrunk={level3ClientId !== null}
      >
        {openTodosData?.isPending ? (
          <OpenTodosSidePanelSkeleton />
        ) : openTodosData ? (
          <OpenTodosSidePanel data={openTodosData} onTodoClick={handleLevel3Open} />
        ) : null}
      </ManagementSidepanel>

      <ManagementSidepanel isOpen={level3ClientId !== null} onClose={handleCloseLevel3} level={3}>
        {level3ClientId && (
          <TodoSidePanel
            clientId={level3ClientId}
            onTodoToggle={() => undefined}
            onCustomerClick={handleCustomerClickFromEmployee}
          />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel
        isOpen={selectedCustomerIdFromEmployee !== null}
        onClose={handleCloseCustomerLevel1}
        level={1}
        isLevel1Shrunk={customerActivity !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={customerDetailPeriod.period}
            customDateRange={customerDetailPeriod.customDateRange}
            onPeriodChange={customerDetailPeriod.setPeriod}
            onCustomDateRangeChange={customerDetailPeriod.setCustomDateRange}
          />
        }
      >
        {selectedCustomerIdFromEmployee && (
          <CustomerDetailsSidePanel
            clientId={selectedCustomerIdFromEmployee}
            selectedPeriod={customerDetailPeriod.period}
            selectedCustomDateRange={customerDetailPeriod.customDateRange}
            onRecapCountClick={handleCustomerRecapsClick}
            onProductGroupOpen={handleCustomerProductGroupOpen}
            onEmployeeClick={handleEmployeeClickFromCustomer}
          />
        )}
      </ManagementSidepanel>

      <ProductDrilldownSidePanel
        isOpen={customerActivity === "productGroup" && customerActivityContext !== null}
        onClose={handleCloseCustomerLevel2}
        isShrunk={customerLevel3ClientId !== null}
        root={customerProductDrillRoot}
        clientId={selectedCustomerIdFromEmployee}
        customerName={selectedCustomerFromEmployee?.name ?? ""}
        headerControls={
          <DateRangeSelector
            selectedPeriod={customerLevel2Period.period}
            customDateRange={customerLevel2Period.customDateRange}
            onPeriodChange={customerLevel2Period.setPeriod}
            onCustomDateRangeChange={customerLevel2Period.setCustomDateRange}
          />
        }
        selectedPeriod={customerLevel2Period.period}
        selectedCustomDateRange={customerLevel2Period.customDateRange}
      />

      <ManagementSidepanel
        isOpen={customerActivity === "completedRecaps"}
        onClose={handleCloseCustomerLevel2}
        level={2}
        isLevel2Shrunk={customerLevel3ClientId !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={customerLevel2Period.period}
            customDateRange={customerLevel2Period.customDateRange}
            onPeriodChange={customerLevel2Period.setPeriod}
            onCustomDateRangeChange={customerLevel2Period.setCustomDateRange}
          />
        }
      >
        {selectedCustomerIdFromEmployee && selectedCustomerFromEmployee && (
          <CustomerRecapsSidePanel
            clientId={selectedCustomerFromEmployee.id}
            customerName={selectedCustomerFromEmployee.name}
            selectedPeriod={customerLevel2Period.period}
            selectedCustomDateRange={customerLevel2Period.customDateRange}
            onRecapClick={handleCustomerRecapAppointmentClick}
          />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel isOpen={customerLevel3ClientId !== null} onClose={handleCloseCustomerLevel3} level={3}>
        {customerLevel3ClientId && (
          <TodoSidePanel clientId={customerLevel3ClientId} note={selectedNote} onTodoToggle={() => undefined} />
        )}
      </ManagementSidepanel>
    </>
  );
};
